import {announce} from '@github-ui/aria-live'
import type {CompletedReview, CopilotReviewerRow} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {Link} from '@github-ui/react-router'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {SyncIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useRef} from 'react'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useReRequestReviewMutation} from '../../../../mutations/use-re-request-review-mutation'
import {pullRequestsChangesWithRangeRoute} from '../../../../routes/files-changed-routes'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {CopilotReviewEffortMenu} from '../CopilotReviewEffortMenu'
import styles from '../Reviewers.module.css'
import {ReviewerIdentity, reviewerDisplayName} from '../ReviewerIdentity'
import {reviewState, reviewStateTooltip} from '../review-state-helpers'
import {useStagedCopilotReviewEffort} from '../use-staged-copilot-review-effort'

export function CompletedReviewRow({
  review,
  muted = false,
  copilotReviewerRow,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  review: CompletedReview
  muted?: boolean
  copilotReviewerRow?: CopilotReviewerRow
  onMutationStart?: () => void
  onMutationError?: (origin: Element | null) => void
  onMutationSuccess?: () => void
  onCloseWithoutTarget?: () => void
}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const mutationOriginRef = useRef<Element | null>(null)
  const {mutate: reRequestReview, isPending} = useReRequestReviewMutation({
    onError: () => onMutationError?.(mutationOriginRef.current),
    onSuccess: onMutationSuccess,
  })
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const canReRequestReview = review.canReRequest && (copilotReviewerRow?.requestable ?? true)
  const {shownEffort, explicitEffort, stageEffort, captureRequest} = useStagedCopilotReviewEffort(
    copilotReviewerRow,
    canReRequestReview,
  )
  const reviewer = review.reviewer
  if (!reviewer) return null

  const name = reviewerDisplayName(reviewer)
  const {icon: stateIcon, className: stateClass} = reviewState(review.state, review.dismissedReviewState)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })
  const requestReview = () => {
    if (isPending) return
    mutationOriginRef.current = document.activeElement
    onMutationStart?.()
    announce(`Re-requesting review from ${name}`)
    reRequestReview({
      reviewerId: Number(reviewer.id),
      copilotReviewEffortRequest: captureRequest(),
    })
  }

  return (
    <li className={clsx(styles.reviewerRow, copilotReviewerRow && styles.withEffortMenu)}>
      <span className={styles.reviewerIdentityGroup}>
        <ReviewerIdentity reviewer={reviewer} />
        {copilotReviewerRow ? (
          <span className={styles.effortMenu}>
            <CopilotReviewEffortMenu
              row={copilotReviewerRow}
              selectedEffort={shownEffort}
              disabled={isPending || !canReRequestReview}
              onCloseWithoutTarget={onCloseWithoutTarget}
              onSelectEffort={effort => {
                sendPullRequestAnalyticsEvent('overview.copilot_review_effort_select', 'OVERVIEW_REVIEWER', {
                  review_effort: effort,
                })
                stageEffort(effort)
              }}
            />
          </span>
        ) : null}
      </span>
      <span className={styles.indicators}>
        {canReRequestReview && (
          <IconButton
            icon={SyncIcon}
            aria-label={`Re-request review from ${name}`}
            variant="invisible"
            size="small"
            inactive={isPending || undefined}
            aria-disabled={isPending || undefined}
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
            onClick={() => {
              if (isPending) return
              sendPullRequestAnalyticsEvent(
                'overview.re_request_review',
                'OVERVIEW_REVIEWER',
                copilotReviewerRow
                  ? {
                      review_effort: explicitEffort ?? 'default',
                      review_effort_level_selected: explicitEffort !== undefined,
                    }
                  : undefined,
              )
              requestReview()
            }}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name, review.dismissedReviewState)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, muted ? 'color-fg-muted' : stateClass)}
        />
      </span>
    </li>
  )
}
